import { WINDOW_DRAG_START_EVENT, WINDOW_DRAG_END_EVENT } from './constants';
import { HAS_WINDOW } from '@eb/eds-utils';

/**
 * This set of utils exist to normalize window-level drag event behavior cross-browser.
 * This relates to both the order of drag event invocation as well as several bugs or
 * inconsistencies in certain browsers.
 * For example, Firefox has several well-documented problems with dragenter/dragleave events:
 *    https://bugzilla.mozilla.org/show_bug.cgi?id=656164
 *    https://bugzilla.mozilla.org/show_bug.cgi?id=619703
 *    http://stackoverflow.com/questions/14194324/firefox-firing-dragleave-when-dragging-over-text
 *    http://stackoverflow.com/questions/10253663/how-to-detect-the-dragleave-event-in-firefox-when-dragging-outside-the-window
 */

/**
 * This is the element to which events listeners will be attached.  In practice, this should be
 * the window element, but can be overridden for testing purposes.
 */
let eventElement;

if (HAS_WINDOW) {
    eventElement = window;
}

// A list of domNodes that have received dragenter but not dragleave
let _draggedElements = [];

// A list of handlers assigned from outside this library
let _registeredHandlers = [];

// Invokes all registered handlers when an event is fired
const _invokeRegisteredHandlers = (eventType, event) => {
    _registeredHandlers.forEach((handler) => handler(eventType, event));
};

/**
 * The function that's invoked as the user drags a file around the window.
 * Analogous to mousemove but with a dragged element or file.
 */
const _onDragOver = (event) => event.preventDefault();

/**
 * The function that's invoked when the user first enters an element while dragging.
 * Analogous to mouseover but with a dragged element or file.
 */
const _onDragEnter = (event) => {
    event.preventDefault();

    // if there aren't any dragged elements now, the user has just dragged the file in
    if (_draggedElements.length === 0) {
        _invokeRegisteredHandlers(WINDOW_DRAG_START_EVENT, event);
    }
    _draggedElements = [..._draggedElements, event.target];
};

/**
 * The function that's invoked when the user leaves an element while dragging.
 * Analogous to mouseout but with a dragged element or file.
 */
const _onDragLeave = (event) => {
    event.preventDefault();

    // remove current event target from dragged element list
    _draggedElements = _draggedElements.filter(
        (element) => element !== event.target,
    );

    // if there aren't any dragged elements now, the user has just dragged the file out
    if (_draggedElements.length === 0) {
        _invokeRegisteredHandlers(WINDOW_DRAG_END_EVENT, event);
    }
};

/**
 * The function that's invoked when the user releases a dragged element or file.
 */
const _onDrop = (event) => {
    event.preventDefault();

    _draggedElements = [];
    _invokeRegisteredHandlers(WINDOW_DRAG_END_EVENT, event);
};

/**
 * The public interface to add handlers to be invoked.
 */
export const addWindowDragEvent = (onDragEvent) => {
    // if there aren't any registered handlers here, we need to attach our own
    // window-level handlers to catch events.
    if (_registeredHandlers.length === 0 && eventElement) {
        eventElement.addEventListener('dragover', _onDragOver);
        eventElement.addEventListener('dragenter', _onDragEnter);
        eventElement.addEventListener('dragleave', _onDragLeave);
        eventElement.addEventListener('drop', _onDrop);
    }
    _registeredHandlers = [..._registeredHandlers, onDragEvent];
};

/**
 * The public interface to remove handlers.
 */
export const removeWindowDragEvent = (onDragEvent) => {
    _registeredHandlers = _registeredHandlers.filter(
        (handler) => handler !== onDragEvent,
    );

    // if there aren't any registered handlers here, we don't need to continue listening
    if (_registeredHandlers.length === 0 && eventElement) {
        eventElement.removeEventListener('dragover', _onDragOver);
        eventElement.removeEventListener('dragenter', _onDragEnter);
        eventElement.removeEventListener('dragleave', _onDragLeave);
        eventElement.removeEventListener('drop', _onDrop);
    }
};

/**
 * This function can be used to set an alternate element to receive events.
 * This is useful for testing purposes.
 */
export const specSetMockWindow = (mockWindow) => {
    // if we're resetting the eventElement, we need to clear any previous
    // handlers attached to that element
    _registeredHandlers = [];
    removeWindowDragEvent();

    eventElement = mockWindow;
};
